import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { cn } from '@/lib/utils'
import {
  Loader2,
  AlertCircle,
  Database,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Settings2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useModelData } from '@/hooks/useProjectDatabase'
import { TruncatedCell, formatCellValue, PAGE_SIZES, SEARCH_DEBOUNCE_MS, ROW_HEIGHT, OVERSCAN } from './database-utils'

interface DataViewerProps {
  projectId: number
  appLabel: string | null
  modelName: string | null
}

export function DataViewer({ projectId, appLabel, modelName }: DataViewerProps) {
  const { t } = useTranslation('conversations')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  // Reset pagination when model changes
  const resetState = useCallback(() => {
    setPage(1)
    setSorting([])
    setColumnVisibility({})
    setSearch('')
    setSearchInput('')
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }
  }, [])

  // Build order_by from sorting state
  const orderBy = useMemo(() => {
    if (sorting.length === 0) return undefined
    const { id, desc } = sorting[0]
    return desc ? `-${id}` : id
  }, [sorting])

  // Build options for the hook
  const options = useMemo(() => ({
    page,
    page_size: pageSize,
    order_by: orderBy,
    search: search || undefined,
  }), [page, pageSize, orderBy, search])

  const { data, isLoading, isFetching, error } = useModelData(
    projectId,
    appLabel,
    modelName,
    options
  )

  // Track previous model to detect model switches
  const prevModelRef = useRef({ appLabel, modelName })
  const isModelChange = prevModelRef.current.appLabel !== appLabel ||
                        prevModelRef.current.modelName !== modelName

  // Update ref after data arrives (not on every render)
  useEffect(() => {
    if (!isFetching) {
      prevModelRef.current = { appLabel, modelName }
    }
  }, [appLabel, modelName, isFetching])

  // Reset state when model changes
  useEffect(() => {
    resetState()
  }, [appLabel, modelName, resetState])

  const handleSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }
    setSearch(searchInput)
    setPage(1)
  }, [searchInput])

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInput(value)

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value)
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
  }, [])

  const handleClearSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }
    setSearchInput('')
    setSearch('')
    setPage(1)
  }, [])

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPage(1)
  }

  // Build columns from field names
  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    const fields = data?.fields || []
    return fields.map((field) => ({
      id: field,
      accessorKey: field,
      header: ({ column }) => (
        <button
          className={cn(
            'flex items-center gap-1 w-full text-left',
            'hover:text-foreground transition-colors'
          )}
          onClick={() => column.toggleSorting()}
        >
          <span>{field}</span>
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronsUpDown className="h-3 w-3 opacity-40" />
          )}
        </button>
      ),
      cell: ({ getValue }) => {
        const cellValue = formatCellValue(getValue())
        return <TruncatedCell value={cellValue} />
      },
    }))
  }, [data?.fields])

  // Table data
  const tableData = useMemo(() => data?.data || [], [data?.data])

  // TanStack Table instance
  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      columnVisibility,
    },
    onSortingChange: (updater) => {
      setSorting(updater)
      setPage(1) // Reset to first page on sort
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true, // Server-side sorting
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
  })

  const { rows } = table.getRowModel()

  // Virtual scrolling
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  })

  const virtualRows = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()

  // Padding for virtual scroll
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom = virtualRows.length > 0
    ? totalSize - virtualRows[virtualRows.length - 1].end
    : 0

  // No model selected
  if (!appLabel || !modelName) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Database className="h-12 w-12 mb-3 opacity-30" />
        <span className="text-sm">{t('database.selectModel', 'Select a model to view data')}</span>
      </div>
    )
  }

  // Loading state - show for initial load OR when switching models
  if ((isLoading && !data) || (isFetching && isModelChange)) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mb-2" />
        <span className="text-sm">{t('database.loadingData', 'Loading data...')}</span>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <AlertCircle className="h-6 w-6 text-destructive mb-2" />
        <span className="text-sm text-destructive">{error.message}</span>
      </div>
    )
  }

  // No data
  if (!data?.data?.length && !search) {
    return (
      <div className="flex flex-col h-full">
        <Header
          modelName={modelName}
          totalCount={0}
          searchInput={searchInput}
          onSearchInputChange={handleSearchInputChange}
          onSearch={handleSearch}
          onClearSearch={handleClearSearch}
          columns={table.getAllLeafColumns()}
        />
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
          <Database className="h-12 w-12 mb-3 opacity-30" />
          <span className="text-sm">{t('database.noData', 'No records in this table')}</span>
        </div>
      </div>
    )
  }

  const totalCount = data?.total_count || 0
  const totalPages = Math.ceil(totalCount / pageSize)
  const startRecord = (page - 1) * pageSize + 1
  const endRecord = Math.min(page * pageSize, totalCount)

  return (
    <div className="flex flex-col h-full min-h-0">
      <Header
        modelName={modelName}
        totalCount={totalCount}
        searchInput={searchInput}
        onSearchInputChange={handleSearchInputChange}
        onSearch={handleSearch}
        onClearSearch={handleClearSearch}
        isLoading={isFetching}
        columns={table.getAllLeafColumns()}
      />

      {/* Table container with virtual scrolling */}
      <div
        ref={tableContainerRef}
        className="flex-1 min-h-0 overflow-auto border-t"
      >
        <table className="w-full text-sm table-fixed">
          <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      'px-3 py-2 text-left text-xs font-medium text-muted-foreground',
                      'border-b whitespace-nowrap relative group'
                    )}
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {/* Column resize handle */}
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      className={cn(
                        'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none',
                        'opacity-0 group-hover:opacity-100 hover:bg-primary/50 transition-opacity',
                        header.column.getIsResizing() && 'opacity-100 bg-primary'
                      )}
                    />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: paddingTop }} colSpan={columns.length} />
              </tr>
            )}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index]
              return (
                <tr
                  key={row.id}
                  className="border-b hover:bg-muted/30 transition-colors"
                  style={{ height: ROW_HEIGHT }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-3 py-2 whitespace-nowrap max-w-[300px] overflow-hidden"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              )
            })}
            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: paddingBottom }} colSpan={columns.length} />
              </tr>
            )}
          </tbody>
        </table>

        {/* Empty search results */}
        {rows.length === 0 && search && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Search className="h-8 w-8 mb-2 opacity-30" />
            <span className="text-sm">{t('database.noSearchResults', 'No results found')}</span>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-3 py-2 border-t bg-muted/30 flex-shrink-0">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>
            {t('database.showing', 'Showing')} {startRecord}-{endRecord} {t('database.of', 'of')} {totalCount}
          </span>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="h-7 w-[70px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground px-2">
            {t('database.page', 'Page')} {page} / {totalPages || 1}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// Header component
interface HeaderProps {
  modelName: string
  totalCount: number
  searchInput: string
  onSearchInputChange: (value: string) => void
  onSearch: () => void
  onClearSearch: () => void
  isLoading?: boolean
  columns?: { id: string; getIsVisible: () => boolean; toggleVisibility: (value: boolean) => void }[]
}

function Header({
  modelName,
  totalCount,
  searchInput,
  onSearchInputChange,
  onSearch,
  onClearSearch,
  isLoading,
  columns,
}: HeaderProps) {
  const { t } = useTranslation('conversations')

  return (
    <div className="flex items-center justify-between px-3 h-10 border-b bg-muted/30 flex-shrink-0">
      <div className="flex items-center gap-2">
        <span className="font-medium text-sm">{modelName}</span>
        <span className="text-xs text-muted-foreground">
          ({totalCount} {t('database.records', 'records')})
        </span>
        {isLoading && <Loader2 className="h-3 w-3 animate-spin" />}
      </div>
      <div className="flex items-center gap-2">
        <div className="relative">
          <Input
            placeholder={t('database.searchData', 'Search data...')}
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            className="h-7 w-48 text-xs pr-7"
          />
          {searchInput ? (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={onClearSearch}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          )}
        </div>
        {/* Column visibility dropdown */}
        {columns && columns.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <Settings2 className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-xs">
                {t('database.toggleColumns', 'Toggle columns')}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columns.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={column.getIsVisible()}
                  onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  className="text-xs"
                >
                  {column.id}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}
